// client/src/services/reportsApi.js
// Custom reporting engine: server/routes/reportRoutes.js, mounted at
// /api/reports. The tenant always comes from the session cookie on the
// server, so nothing here sends a tenant id.

import { getJson, postJson, putJson, delJson } from './http';

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';
const base = '/api/reports';

/** { role, permissions: {create, schedule, export, view_shared, view_pii, manage}, limits, config? } */
export const getReportPermissions = () => getJson(`${base}/permissions`);
/** Manage only. permissions = { action: [roles] } */
export const saveReportPermissions = (permissions) => putJson(`${base}/permissions`, { permissions });

/** { datasets: [{ key, label, module, description, heavy, params, fields: [...] }], timezone } */
export const getDatasets = () => getJson(`${base}/datasets`);

/**
 * Run one page. Body has one of definition / reportId / templateKey, plus
 * page / pageSize. Resolves to the page, or { queued: true, runId } (202)
 * for heavy datasets that run in the background.
 */
export const previewReport = (body) => postJson(`${base}/preview`, body);

/** { reports, templates, permissions, migrationPending? } */
export const listReports = () => getJson(base);
export const getReport = (id) => getJson(`${base}/${Number(id)}`);
export const createReport = (body) => postJson(base, body);
export const updateReport = (id, body) => putJson(`${base}/${Number(id)}`, body);
export const deleteReport = (id) => delJson(`${base}/${Number(id)}`);
export const duplicateReport = (id) => postJson(`${base}/${Number(id)}/duplicate`, {});

/** Locked tenant row for a system template, so it can be exported / scheduled. */
export const materializeTemplate = (key) => postJson(`${base}/templates/${encodeURIComponent(key)}/materialize`, {});

/** Queue an export → { runId } */
export const exportReport = (id, format) => postJson(`${base}/${Number(id)}/export`, { format });
export const exportDefinition = (definition, format, name) => postJson(`${base}/export`, { definition, format, name });

export const listRuns = () => getJson(`${base}/runs`);
export const getRun = (runId) => getJson(`${base}/runs/${Number(runId)}`);
/** Result of a heavy preview run (format json). */
export const getRunResult = (runId) => getJson(`${base}/runs/${Number(runId)}/download`);
export const runDownloadUrl = (runId) => `${BASE_URL}${base}/runs/${Number(runId)}/download`;

export const getRecipients = () => getJson(`${base}/recipients`);
export const listSchedules = (id) => getJson(`${base}/${Number(id)}/schedules`);
export const createSchedule = (id, body) => postJson(`${base}/${Number(id)}/schedules`, body);
export const updateSchedule = (id, sid, body) => putJson(`${base}/${Number(id)}/schedules/${Number(sid)}`, body);
export const deleteSchedule = (id, sid) => delJson(`${base}/${Number(id)}/schedules/${Number(sid)}`);

/** Server error text for a failed call, or the fallback. */
export function errorText(err, fallback) {
  return err?.response?.data?.error || err?.response?.data?.message || fallback;
}
